import { useState, useMemo, useCallback, Fragment } from "react";
import type { SynthesisStep } from "@/types/chemistry";
import { renderFormula } from "@/lib/chemicalFormula";
import { useToast } from "@/hooks/use-toast";
import ReactionStructure from "@/components/step/chemistry/ReactionStructure";
import { getStepDisplayName } from "@/lib/stepUtils";
import { workDaysBetween } from "@/lib/workDays";
import { useParams, useNavigate } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { useRuns, computeStepTargetDates } from "@/hooks/useRuns";
import { generateMockSupplierOffers } from "@/data/mockSuppliers";
import { AppHeader } from "@/components/AppHeader";
import { RunSettingsModal } from "@/components/run/RunSettingsModal";
import { SupplierDetailContent } from "@/components/supplier/SupplierDetailContent";
import { BOMItemList } from "@/components/supplier/BOMItemList";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Progress } from "@/components/ui/progress";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  AlertCircle, ArrowRight, Calendar as CalendarIcon, Check, ChevronDown, ChevronRight, Clock, Copy, Layers, Package,
  Play, Settings, ShoppingCart, SlidersHorizontal, Table2, TriangleAlert, X,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import type { BOMItem, RunStatus, SupplierOffer } from "@/types/chemistry";

const STATUS_CONFIG: Record<RunStatus, { label: string; color: string }> = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground" },
  configured: { label: "Configured", color: "bg-accent text-accent-foreground" },
  ready: { label: "Ready", color: "bg-success text-success-foreground" },
  in_progress: { label: "In Progress", color: "bg-primary text-primary-foreground" },
  complete: { label: "Complete", color: "bg-success text-success-foreground" },
};

const BOM_STATUS_ICON: Record<string, React.ReactNode> = {
  not_ordered: <Clock className="h-3.5 w-3.5 text-muted-foreground" />,
  selected: <ShoppingCart className="h-3.5 w-3.5 text-warning" />,
  ordered: <Check className="h-3.5 w-3.5 text-success" />,
};

interface GroupHeader {
  key: string;
  label: string;
  targetDate?: string;
  items: BOMItem[];
  totalCost: number;
  latestDelivery?: string;
  hasConflict: boolean;
}

const RunDetail = () => {
  const { projectId, runId } = useParams<{ projectId: string; runId: string }>();
  const navigate = useNavigate();
  const { getProject } = useProjects();
  const { getRun, updateRun, updateBOMItem, regenerateBOM } = useRuns();
  const { toast } = useToast();

  const project = getProject(projectId!);
  const run = getRun(runId!);

  const [stepsViewOpen, setStepsViewOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [editingDeadline, setEditingDeadline] = useState<string | null>(null);

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Sourcing mode state
  const [sourcingMode, setSourcingMode] = useState(false);
  const [bomView, setBomView] = useState<"material" | "steps" | "compounds">("material");
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    name: true,
    formula: true,
    cas: true,
    mw: true,
    qty: true,
    co2: true,
    status: true,
    supplier: true,
    cost: true,
    deadline: true,
    delivery: true,
    packageSize: true,
    units: true,
  });

  const columnDefs: { key: string; label: string }[] = [
    { key: "name", label: "Name" },
    { key: "formula", label: "Formula" },
    { key: "cas", label: "CAS #" },
    { key: "mw", label: "MW (g/mol)" },
    { key: "qty", label: "Qty" },
    { key: "co2", label: "CO₂ kg" },
    { key: "status", label: "Status" },
    { key: "supplier", label: "Supplier" },
    { key: "cost", label: "Cost" },
    { key: "deadline", label: "Deadline" },
    { key: "delivery", label: "Earliest Delivery" },
    { key: "packageSize", label: "Package Size" },
    { key: "units", label: "Units" },
  ];

  // Build a formula/CAS → name lookup from project chemicals so old runs get names
  const chemLookup = useMemo(() => {
    const nameMap = new Map<string, string>();
    const mwMap = new Map<string, number>();
    if (project) {
      project.steps.forEach(step => {
        [...step.reactants, ...step.reagents, ...step.products].forEach(c => {
          if (c.name) {
            if (c.casNumber) nameMap.set(c.casNumber, c.name);
            if (c.formula) nameMap.set(c.formula, c.name);
          }
          if (c.molarWeight) {
            if (c.casNumber) mwMap.set(c.casNumber, c.molarWeight);
            if (c.formula) mwMap.set(c.formula, c.molarWeight);
          }
        });
      });
    }
    return { nameMap, mwMap };
  }, [project]);

  const enrichedBomItems = useMemo(() =>
    run ? run.bomItems.map(item => ({
      ...item,
      chemicalName: item.chemicalName || chemLookup.nameMap.get(item.casNumber) || chemLookup.nameMap.get(item.materialName) || "",
      molarWeight: item.molarWeight ?? chemLookup.mwMap.get(item.casNumber) ?? chemLookup.mwMap.get(item.materialName) ?? undefined,
    })) : [],
    [run, chemLookup]
  );

  // Step ID → label lookup
  const stepLabelMap = useMemo(() => {
    const map = new Map<string, { label: string; index: number }>();
    if (project) {
      project.steps.forEach((s, i) => map.set(s.id, { label: getStepDisplayName(s), index: i }));
    }
    return map;
  }, [project]);

  // Sorted BOM items for "By Materials" view — alphabetical by name, then earliest step
  const sortedBomItems = useMemo(() => {
    return [...enrichedBomItems].sort((a, b) => {
      const nameA = (a.chemicalName || a.materialName).toLowerCase();
      const nameB = (b.chemicalName || b.materialName).toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const idxA = Math.min(...a.stepIds.map(id => stepLabelMap.get(id)?.index ?? Infinity));
      const idxB = Math.min(...b.stepIds.map(id => stepLabelMap.get(id)?.index ?? Infinity));
      return idxA - idxB;
    });
  }, [enrichedBomItems, stepLabelMap]);

  // Compute overall yield as multiplicative product of included step yields
  const overallYield = useMemo(() => {
    if (!project || !run) return 0;
    const orderedSteps = project.steps.filter(s => run.selectedStepIds.includes(s.id));
    return orderedSteps.reduce((acc, step) => {
      const overrideYield = run.stepOverrides?.[step.id]?.yield;
      const stepYield = overrideYield ?? (step.yield ? parseFloat(step.yield) : run.assumedYield);
      return acc * (stepYield / 100);
    }, 1) * 100;
  }, [project, run]);

  // Earliest start date = latest "earliest delivery" across all BOM items (all must arrive)
  const earliestStartDate = useMemo(() => {
    const deliveryDates = enrichedBomItems
      .map(i => i.earliestDelivery)
      .filter(Boolean) as string[];
    if (deliveryDates.length === 0) return undefined;
    return deliveryDates.sort().reverse()[0];
  }, [enrichedBomItems]);

  // Earliest completion = earliest start + sum of step times
  const earliestCompletionDate = useMemo(() => {
    if (!earliestStartDate || !project || !run) return undefined;
    const orderedSteps = project.steps.filter(s => run.selectedStepIds.includes(s.id));
    const totalDays = orderedSteps.reduce((sum, step) => {
      const overrideDays = run.stepOverrides?.[step.id]?.stepTimeDays;
      return sum + (overrideDays ?? (step.stepTime ? parseInt(step.stepTime) || 0 : 0));
    }, 0);
    const d = new Date(earliestStartDate);
    d.setDate(d.getDate() + totalDays);
    return d.toISOString().split("T")[0];
  }, [earliestStartDate, project, run]);

  if (!project || !run) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">{!project ? "Compound" : "Run"} not found</p>
        </div>
      </div>
    );
  }

  // Cost: "needed" = pricePerUnit * quantity; "purchase" = selectedCost (package-based)
  const costNeeded = enrichedBomItems.reduce((s, b) => s + (b.pricePerUnit ? b.pricePerUnit * b.quantity : 0), 0);
  const costPurchase = enrichedBomItems.reduce((s, b) => s + (b.selectedCost ?? 0), 0);
  const totalCost = enrichedBomItems.reduce((s, b) => s + (b.selectedCost ?? (b.pricePerUnit ? b.pricePerUnit * b.quantity : 0)), 0);
  const totalCo2 = enrichedBomItems.reduce((s, b) => s + (b.co2eq ? b.co2eq * b.quantity : 0), 0);
  const suppliersSelected = enrichedBomItems.filter(b => b.supplierName).length;

  // Date color coding helper
  const getDateColor = (actual: string | undefined, earliest: string | undefined): string => {
    if (!actual || !earliest) return "text-muted-foreground";
    const buffer = workDaysBetween(new Date(earliest), new Date(actual));
    if (buffer >= 3) return "text-success";
    if (buffer >= 0) return "text-warning";
    return "text-destructive";
  };

  const allSuppliersSelected = suppliersSelected === enrichedBomItems.length && enrichedBomItems.length > 0;

  // Compute CO₂ thresholds relative to data range
  const co2Values = enrichedBomItems.filter(b => b.co2eq != null).map(b => b.co2eq! * b.quantity);
  const maxCo2 = co2Values.length > 0 ? Math.max(...co2Values) : 1;
  const co2LowThreshold = maxCo2 * 0.33;
  const co2HighThreshold = maxCo2 * 0.66;

  const stepTargetDates = computeStepTargetDates(
    project, run.selectedStepIds, run.deadline, run.stepOverrides
  );

  const lateItems = enrichedBomItems.filter(item => {
    if (!item.deliveryDays || !item.targetDate) return false;
    const arrival = new Date();
    arrival.setDate(arrival.getDate() + item.deliveryDays);
    return arrival > new Date(item.targetDate);
  });

  // Helper: find root step for a given step id (walk up parentId chain)
  const findRootStep = (stepId: string): SynthesisStep | undefined => {
    let step = project.steps.find(s => s.id === stepId);
    while (step?.parentId) {
      step = project.steps.find(s => s.id === step!.parentId);
    }
    return step;
  };

  // Group BOM items by primary step
  const stepGroups: GroupHeader[] = (() => {
    const orderedSteps = project.steps
      .filter(s => run.selectedStepIds.includes(s.id))
      .sort((a, b) => {
        const parseLabel = (l: string) => {
          const match = l.replace(/^Step\s*/i, "").match(/^(\d+)(.*)$/);
          return match ? { num: parseInt(match[1]), suffix: match[2] } : { num: 0, suffix: l };
        };
        const pa = parseLabel(a.label);
        const pb = parseLabel(b.label);
        return pa.num !== pb.num ? pa.num - pb.num : pa.suffix.localeCompare(pb.suffix);
      });
    const groupMap = new Map<string, BOMItem[]>();
    orderedSteps.forEach(s => groupMap.set(s.id, []));
    enrichedBomItems.forEach(item => {
      const primaryStepId = item.stepIds[0];
      if (!groupMap.has(primaryStepId)) groupMap.set(primaryStepId, []);
      groupMap.get(primaryStepId)!.push(item);
    });

    return Array.from(groupMap.entries())
      .filter(([, items]) => items.length > 0)
      .map(([stepId, items]) => {
        const step = project.steps.find(s => s.id === stepId);
        const overrideDeadline = run.stepOverrides?.[stepId]?.deadline;
        const targetDate = overrideDeadline || stepTargetDates[stepId];
        const cost = items.reduce((s, b) => s + (b.selectedCost ?? 0), 0);
        const deliveryDates = items.map(i => i.earliestDelivery).filter(Boolean) as string[];
        const latestDelivery = deliveryDates.length > 0 ? deliveryDates.sort().reverse()[0] : undefined;
        const hasConflict = !!(targetDate && latestDelivery && latestDelivery > targetDate);
        return { key: stepId, label: step ? getStepDisplayName(step) : stepId, targetDate, items, totalCost: cost, latestDelivery, hasConflict };
      });
  })();

  // Group BOM items by compound (root step's product)
  const compoundGroups: GroupHeader[] = (() => {
    const groupMap = new Map<string, { label: string; items: BOMItem[] }>();

    enrichedBomItems.forEach(item => {
      const primaryStepId = item.stepIds[0];
      const rootStep = findRootStep(primaryStepId);
      const compoundKey = rootStep?.id || "unknown";
      const compoundLabel = rootStep?.products?.[0]?.name || rootStep?.name || "Unknown Compound";

      if (!groupMap.has(compoundKey)) {
        groupMap.set(compoundKey, { label: compoundLabel, items: [] });
      }
      groupMap.get(compoundKey)!.items.push(item);
    });

    return Array.from(groupMap.entries())
      .filter(([, g]) => g.items.length > 0)
      .map(([compoundKey, g]) => {
        const cost = g.items.reduce((s, b) => s + (b.selectedCost ?? 0), 0);
        const deliveryDates = g.items.map(i => i.earliestDelivery).filter(Boolean) as string[];
        const latestDelivery = deliveryDates.length > 0 ? deliveryDates.sort().reverse()[0] : undefined;
        const targetDates = g.items.map(i => i.targetDate).filter(Boolean) as string[];
        const targetDate = targetDates.length > 0 ? targetDates.sort()[0] : undefined;
        const hasConflict = !!(targetDate && latestDelivery && latestDelivery > targetDate);
        return { key: compoundKey, label: g.label, targetDate, items: g.items, totalCost: cost, latestDelivery, hasConflict };
      });
  })();

  const activeGroups = bomView === "steps" ? stepGroups : compoundGroups;

  // Flat list of all BOM items in step order
  const flatItems = stepGroups.flatMap(g => g.items);

  // Ensure offers are populated for active item
  const getItemWithOffers = (item: BOMItem): BOMItem => {
    if (item.supplierOffers?.length) return item;
    return { ...item, supplierOffers: generateMockSupplierOffers(item.materialName, item.casNumber || "") };
  };

  const activeItem = activeItemId ? flatItems.find(i => i.id === activeItemId) : null;
  const activeItemWithOffers = activeItem ? getItemWithOffers(activeItem) : null;
  const activeIndex = activeItemId ? flatItems.findIndex(i => i.id === activeItemId) : -1;

  // checklist defined after handlers below

  const statusConf = STATUS_CONFIG[run.status];

  const handleMarkReady = () => {
    updateRun(run.id, { status: "ready" as RunStatus });
  };

  const handleStartSourcing = () => {
    setSourcingMode(true);
    // Start with first unsourced item, or first item
    const firstUnsourced = flatItems.find(i => !i.supplierName);
    setActiveItemId(firstUnsourced?.id ?? flatItems[0]?.id ?? null);
  };

  const handleExitSourcing = () => {
    setSourcingMode(false);
    setActiveItemId(null);
  };

  const checklist = [
    { label: "Parameters", done: true, action: undefined as (() => void) | undefined },
    { label: "BOM", done: enrichedBomItems.length > 0, action: undefined as (() => void) | undefined },
    { label: "Suppliers", done: allSuppliersSelected, action: !allSuppliersSelected && enrichedBomItems.length > 0 ? handleStartSourcing : undefined },
    { label: "Ready", done: run.status === "ready" || run.status === "in_progress" || run.status === "complete", action: allSuppliersSelected && run.status === "draft" ? handleMarkReady : undefined },
  ];

  const handleSelectSupplier = (itemId: string, offer: SupplierOffer) => {
    updateBOMItem(run.id, itemId, {
      supplierName: offer.supplierName,
      supplierId: offer.id,
      status: "selected",
      pricePerUnit: offer.costPerUnit,
      packageSize: offer.packageSizeChosen,
      selectedCost: offer.cost,
      earliestDelivery: offer.earliestDelivery,
      co2eq: offer.co2ePerGram,
    });
  };

  const handleNavigate = (direction: "prev" | "next" | "skip") => {
    if (direction === "prev" && activeIndex > 0) {
      setActiveItemId(flatItems[activeIndex - 1].id);
    } else if (direction === "next" && activeIndex < flatItems.length - 1) {
      setActiveItemId(flatItems[activeIndex + 1].id);
    } else if (direction === "skip") {
      // Skip to next unsourced
      for (let i = activeIndex + 1; i < flatItems.length; i++) {
        if (!flatItems[i].supplierName) {
          setActiveItemId(flatItems[i].id);
          return;
        }
      }
      // If none after, go to next item
      if (activeIndex < flatItems.length - 1) {
        setActiveItemId(flatItems[activeIndex + 1].id);
      }
    }
  };

  const toggleGroup = (stepId: string) => {
    setCollapsedGroups(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const handleStepDeadlineChange = (stepId: string, date: string) => {
    const overrides = { ...(run.stepOverrides || {}) };
    overrides[stepId] = { ...overrides[stepId], deadline: date || undefined };
    updateRun(run.id, { stepOverrides: overrides });
    setEditingDeadline(null);
  };

  const handleRowClick = (item: BOMItem) => {
    setSourcingMode(true);
    setActiveItemId(item.id);
  };

  // ─── Sourcing Mode Layout ─────────────────────────────────
  if (sourcingMode && enrichedBomItems.length > 0) {
    const progressPct = enrichedBomItems.length > 0 ? (suppliersSelected / enrichedBomItems.length) * 100 : 0;
    const sortedBomItems = [...enrichedBomItems].sort((a, b) => {
      const nameA = (a.chemicalName || a.materialName).toLowerCase();
      const nameB = (b.chemicalName || b.materialName).toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const stepA = Math.min(...a.stepIds.map(Number).filter(n => !isNaN(n)));
      const stepB = Math.min(...b.stepIds.map(Number).filter(n => !isNaN(n)));
      return stepA - stepB;
    });

    return (
      <div className="h-screen flex flex-col bg-background">
        <AppHeader />
        {/* Progress strip */}
        <div className="px-6 py-3 border-b flex items-center gap-4 shrink-0">
          <div className="flex-1 flex items-center gap-3">
            <Progress value={progressPct} className="h-2 flex-1 max-w-xs" />
            <span className="text-xs text-muted-foreground font-medium">
              {suppliersSelected} / {enrichedBomItems.length} sourced
            </span>
          </div>
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={handleExitSourcing}>
            <X className="h-3.5 w-3.5" /> Exit Sourcing
          </Button>
          {allSuppliersSelected && (
            <Button size="sm" className="gap-1.5" onClick={handleMarkReady}>
              <Package className="h-3.5 w-3.5" /> Mark as Ready
            </Button>
          )}
        </div>

        {/* Split view */}
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          <ResizablePanel defaultSize={22} minSize={15} maxSize={35}>
            <BOMItemList
              items={sortedBomItems}
              activeItemId={activeItemId}
              onSelectItem={setActiveItemId}
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={78}>
            {activeItemWithOffers ? (
              <SupplierDetailContent
                item={activeItemWithOffers}
                onSelectSupplier={handleSelectSupplier}
                onPrevious={() => handleNavigate("prev")}
                onNext={() => handleNavigate("next")}
                onSkip={() => handleNavigate("skip")}
                hasPrevious={activeIndex > 0}
                hasNext={activeIndex < flatItems.length - 1}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                Select a material from the list to view supplier offers
              </div>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }


  // ─── Standard Layout ─────────────────────────────────────
  const renderBOMRow = (item: BOMItem, showSteps?: boolean) => {
    const isMultiStep = item.stepIds.length > 1;
    const stepLabels = showSteps
      ? item.stepIds
          .map(id => stepLabelMap.get(id))
          .filter(Boolean)
          .sort((a, b) => a!.index - b!.index)
          .map(s => s!.label)
      : [];

    return (
      <TableRow key={item.id} className="cursor-pointer hover:bg-accent/50 whitespace-nowrap" onClick={() => handleRowClick(item)}>
        {visibleColumns.name && (
          <TableCell className="text-sm pl-8">{item.chemicalName || "—"}</TableCell>
        )}
        {visibleColumns.formula && (
          <TableCell className="text-sm font-mono">
            {renderFormula(item.materialName)}
            {!showSteps && isMultiStep && (
              <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">
                <Layers className="h-2.5 w-2.5 mr-0.5" />{item.stepIds.length} steps
              </Badge>
            )}
          </TableCell>
        )}
        {visibleColumns.cas && (
          <TableCell className="text-sm">
            <span
              className="inline-flex items-center gap-1 group/copy cursor-pointer"
              onClick={(e) => { e.stopPropagation(); if (item.casNumber) { navigator.clipboard.writeText(item.casNumber); toast({ title: "Copied", description: item.casNumber }); } }}
            >
              {item.casNumber || "—"}
              {item.casNumber && <Copy className="h-3 w-3 opacity-0 group-hover/copy:opacity-100 transition-opacity text-muted-foreground" />}
            </span>
          </TableCell>
        )}
        {visibleColumns.mw && (
          <TableCell className="text-sm">
            <span
              className="inline-flex items-center gap-1 group/copy cursor-pointer"
              onClick={(e) => { e.stopPropagation(); if (item.molarWeight != null) { navigator.clipboard.writeText(item.molarWeight.toFixed(2)); toast({ title: "Copied", description: `${item.molarWeight.toFixed(2)} g/mol` }); } }}
            >
              {item.molarWeight != null ? item.molarWeight.toFixed(2) : "—"}
              {item.molarWeight != null && <Copy className="h-3 w-3 opacity-0 group-hover/copy:opacity-100 transition-opacity text-muted-foreground" />}
            </span>
          </TableCell>
        )}
        {showSteps && (
          <TableCell className="text-sm">
            <div className="flex flex-wrap gap-1">
              {stepLabels.map(label => (
                <Badge key={label} variant="outline" className="text-[10px] px-1.5 py-0">{label}</Badge>
              ))}
            </div>
          </TableCell>
        )}
        {visibleColumns.qty && (
          <TableCell className="text-sm">{item.quantity.toFixed(2)} {item.unit}</TableCell>
        )}
        {visibleColumns.co2 && (
          <TableCell className="text-sm">
            {(() => {
              const raw = item.co2eq;
              if (raw == null || raw === 0) {
                return (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/30 shrink-0" />
                    <span className="text-muted-foreground">—</span>
                  </span>
                );
              }
              const total = raw * item.quantity;
              const dotColor = total <= co2LowThreshold ? "bg-success" : total <= co2HighThreshold ? "bg-warning" : "bg-destructive";
              const badgeColor = total <= co2LowThreshold
                ? "bg-success/15 text-success"
                : total <= co2HighThreshold
                ? "bg-warning/15 text-warning"
                : "bg-destructive/15 text-destructive";
              return (
                <Badge variant="secondary" className={`text-[10px] font-medium gap-1.5 border-0 ${badgeColor}`}>
                  <span className={`h-2 w-2 rounded-full ${dotColor} shrink-0`} />
                  {total.toFixed(2)} kg
                </Badge>
              );
            })()}
          </TableCell>
        )}
        {visibleColumns.status && (
          <TableCell>
            <div className="flex items-center gap-1.5">
              {BOM_STATUS_ICON[item.status]}
              <span className="text-xs capitalize">{item.status === "not_ordered" ? "Not selected" : item.status.replace("_", " ")}</span>
            </div>
          </TableCell>
        )}
        {visibleColumns.supplier && (
          <TableCell className="text-sm">
            {item.supplierName
              ? <Badge variant="secondary" className="text-[10px]">{item.supplierName}</Badge>
              : <span className="text-xs text-primary underline underline-offset-2">Select supplier</span>}
          </TableCell>
        )}
        {visibleColumns.cost && (
          <TableCell className="text-sm">{item.selectedCost != null ? `€${item.selectedCost.toFixed(2)}` : "—"}</TableCell>
        )}
        {visibleColumns.deadline && (
          <TableCell className="text-sm">
            {(() => {
              const primaryStepId = item.stepIds[0];
              const deadline = item.targetDate
                || run.stepOverrides?.[primaryStepId]?.deadline
                || stepTargetDates[primaryStepId];
              return deadline ? format(parseISO(deadline), "MMM d, yyyy") : "—";
            })()}
          </TableCell>
        )}
        {visibleColumns.delivery && (
          <TableCell className="text-sm">{item.earliestDelivery ? format(parseISO(item.earliestDelivery), "MMM d, yyyy") : "—"}</TableCell>
        )}
        {visibleColumns.packageSize && (
          <TableCell className="text-sm">
            {item.packageSize ? <Badge variant="outline" className="text-[10px]">{item.packageSize}</Badge> : "—"}
          </TableCell>
        )}
        {visibleColumns.units && (
          <TableCell className="text-sm text-center">
            {(() => {
              if (!item.packageSize || !item.quantity) return "—";
              const pkgGrams = parseFloat(item.packageSize);
              if (!pkgGrams) return "—";
              let qtyInGrams = item.quantity;
              if (item.unit === "kg") qtyInGrams = item.quantity * 1000;
              else if (item.unit === "mg") qtyInGrams = item.quantity / 1000;
              const pkgUnit = item.packageSize.replace(/[\d.]/g, "").trim().toLowerCase();
              let pkgInGrams = pkgGrams;
              if (pkgUnit === "kg") pkgInGrams = pkgGrams * 1000;
              else if (pkgUnit === "mg") pkgInGrams = pkgGrams / 1000;
              return Math.ceil(qtyInGrams / pkgInGrams);
            })()}
          </TableCell>
        )}
        <TableCell />
      </TableRow>
    );
  };

  const renderGroupHeader = (group: GroupHeader) => {
    const isCollapsed = collapsedGroups[group.key];
    const isEditing = editingDeadline === group.key;
    const suppCount = group.items.filter(i => i.supplierName).length;

    return (
      <TableRow
        key={`group-${group.key}`}
        className="bg-muted/60 hover:bg-muted/80 cursor-pointer"
        onClick={() => toggleGroup(group.key)}
      >
        {(() => {
          const preDeadlineCols = [
            visibleColumns.name, visibleColumns.formula, visibleColumns.cas, visibleColumns.mw,
            ...(bomView === "material" ? [true] : []),
            visibleColumns.qty, visibleColumns.co2, visibleColumns.status, visibleColumns.supplier, visibleColumns.cost,
          ].filter(Boolean).length;
          const postDeadlineCols = [
            visibleColumns.delivery, visibleColumns.packageSize, visibleColumns.units, true,
          ].filter(Boolean).length;
          return (
            <>
              <TableCell colSpan={preDeadlineCols} className="py-2.5">
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className="text-sm font-semibold">{group.label}</span>
                  <Badge variant="outline" className="text-[10px] font-normal">{group.items.length} material{group.items.length !== 1 ? "s" : ""}</Badge>
                  {suppCount > 0 && suppCount === group.items.length && (
                    <Badge variant="secondary" className="text-[10px] bg-success/15 text-success border-success/30">
                      <Check className="h-2.5 w-2.5 mr-0.5" /> All sourced
                    </Badge>
                  )}
                </div>
              </TableCell>
              {visibleColumns.deadline && (
                <TableCell className="py-2.5" onClick={e => e.stopPropagation()}>
                  {group.targetDate ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap text-primary">
                      <CalendarIcon className="h-3 w-3 shrink-0" />
                      {format(parseISO(group.targetDate), "MMM d, yyyy")}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">—</span>
                  )}
                </TableCell>
              )}
              {postDeadlineCols > 0 && <TableCell colSpan={postDeadlineCols} className="py-2.5" />}
            </>
          );
        })()}
      </TableRow>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-6 py-6">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Synthesis Plans</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href={`/project/${project.id}`}>{project.name}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{run.title}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold tracking-tight">{run.title}</h1>
            
          </div>
          <div className="flex items-center gap-2">
            {enrichedBomItems.length > 0 && (
              <Button variant="default" size="sm" className="gap-1.5" onClick={handleStartSourcing}>
                <ShoppingCart className="h-3.5 w-3.5" /> Start Sourcing
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-3.5 w-3.5" /> Settings
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4 text-sm text-muted-foreground mb-6 border-b pb-4">
          {/* Run-level metrics */}
          <div className="flex items-center gap-8">
            {/* (1) Start Date / Deadline */}
            {(run.startDate || run.deadline) && (() => {
              const dotColor = run.deadline
                ? getDateColor(run.deadline, earliestCompletionDate)
                : getDateColor(run.startDate, earliestStartDate);
              const dotBg = dotColor === "text-success" ? "bg-success" : dotColor === "text-warning" ? "bg-warning" : dotColor === "text-destructive" ? "bg-destructive" : "bg-muted-foreground";
              return (
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className={cn("inline-block h-2 w-2 rounded-full", dotBg)} />
                    {run.deadline ? "Deadline" : "Start Date"}
                  </span>
                  <strong className="text-foreground">
                    {run.deadline
                      ? format(parseISO(run.deadline), "MMM d, yyyy")
                      : format(parseISO(run.startDate), "MMM d, yyyy")}
                  </strong>
                </div>
              );
            })()}

            {/* Estimated end date (if start date set) or estimated start (if deadline set) */}
            {earliestCompletionDate && !run.deadline && (
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Est. End Date</span>
                <strong className="text-foreground">{format(parseISO(earliestCompletionDate), "MMM d, yyyy")}</strong>
              </div>
            )}
            {earliestStartDate && run.deadline && (
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Est. Start Date</span>
                <strong className="text-foreground">{format(parseISO(earliestStartDate), "MMM d, yyyy")}</strong>
              </div>
            )}

            {/* (2) Estimated cost */}
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Est. Cost</span>
              <strong className="text-foreground">€{costNeeded.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</strong>
            </div>

            {/* (3) Estimated CO₂ */}
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Est. CO₂</span>
              <strong className="text-foreground">{totalCo2.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg</strong>
            </div>

            {/* (4) Process Steps — clickable metric */}
            <div
              className="flex flex-col cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setStepsViewOpen(true)}
            >
              <span className="text-xs text-muted-foreground">Process Steps</span>
              <strong className="text-foreground flex items-center gap-1">
                {run.selectedStepIds.length} step{run.selectedStepIds.length !== 1 ? "s" : ""}
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </strong>
            </div>
          </div>

          {/* Per-compound metrics */}
          {(() => {
            const rootSteps = project.steps.filter(s => !s.parentId && run.selectedStepIds.includes(s.id));
            // Also include root steps whose descendants are selected
            const allRoots = project.steps.filter(s => {
              if (s.parentId) return false;
              const getDescendants = (id: string): string[] => {
                const children = project.steps.filter(c => c.parentId === id);
                return children.flatMap(c => [c.id, ...getDescendants(c.id)]);
              };
              const branchIds = [s.id, ...getDescendants(s.id)];
              return branchIds.some(id => run.selectedStepIds.includes(id));
            });

            if (allRoots.length <= 1 && !run.compoundAmounts) {
              // Single compound — show inline
              return (
                <div className="flex items-center gap-8">
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Target Amount</span>
                    <strong className="text-foreground">{run.productAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {run.productUnit}</strong>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-muted-foreground">Total Yield</span>
                    <strong className="text-foreground">{Math.round(overallYield)}%</strong>
                  </div>
                </div>
              );
            }

            // Multiple compounds — show each with its amount and yield
            return (
              <div className="flex items-center gap-6">
                {allRoots.map(root => {
                  const compoundName = root.products?.[0]?.name || root.name;
                  const amount = run.compoundAmounts?.[root.id];
                  // Compute yield for this compound's branch
                  const getDescendants = (id: string): string[] => {
                    const children = project.steps.filter(c => c.parentId === id);
                    return children.flatMap(c => [c.id, ...getDescendants(c.id)]);
                  };
                  const branchIds = [root.id, ...getDescendants(root.id)].filter(id => run.selectedStepIds.includes(id));
                  const branchYield = branchIds.reduce((acc, sid) => {
                    const overrideYield = run.stepOverrides?.[sid]?.yield;
                    const step = project.steps.find(s => s.id === sid);
                    const stepYield = overrideYield ?? (step?.yield ? parseFloat(step.yield) : run.assumedYield);
                    return acc * (stepYield / 100);
                  }, 1) * 100;

                  return (
                    <div key={root.id} className="flex items-center gap-3 rounded-md border px-3 py-1.5">
                      <span className="text-sm font-medium text-foreground">{compoundName}</span>
                      <span className="text-xs text-muted-foreground">
                        {amount != null ? `${amount} ${run.productUnit}` : "—"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Yield: {Math.round(branchYield)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        <div className="mb-6 flex items-center gap-3">
          {checklist.map((c, i) => {
            const isDone = c.done;
            return (
              <Fragment key={i}>
                {i > 0 && <div className={`w-4 h-0.5 ${checklist[i - 1].done ? "bg-success" : "bg-border"}`} />}
                {c.action && !isDone ? (
                  <button
                    onClick={c.action}
                    className="flex items-center gap-1.5 text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors cursor-pointer"
                  >
                    <div className="w-2.5 h-2.5 rounded-full border-2 border-primary bg-background" />
                    {c.label} →
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {isDone ? (
                      <div className="w-2.5 h-2.5 rounded-full bg-success flex items-center justify-center">
                        <Check className="h-1.5 w-1.5 text-success-foreground" />
                      </div>
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full border border-muted-foreground/40 bg-background" />
                    )}
                    <span className={`text-xs ${isDone ? "text-foreground" : "text-muted-foreground"}`}>
                      {c.label}
                    </span>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>

        {lateItems.length > 0 && (
          <div className="mb-6 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Delivery conflict</p>
              <p className="text-xs text-muted-foreground">{lateItems.length} material(s) won't arrive before their step target date.</p>
            </div>
          </div>
        )}


        {/* BOM Table */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Bill of Materials</h2>
          </div>
          <div className="flex items-center gap-1 mb-3 border-b border-border">
            <Table2 className="h-3.5 w-3.5 text-muted-foreground mr-1.5" />
            <button
              className={cn(
                "px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px",
                bomView === "material"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setBomView("material")}
            >
              By Material
            </button>
            <button
              className={cn(
                "px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px",
                bomView === "steps"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setBomView("steps")}
            >
              By Step
            </button>
            <button
              className={cn(
                "px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px",
                bomView === "compounds"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setBomView("compounds")}
            >
              By Compound
            </button>
          </div>
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.name && <TableHead className="text-xs whitespace-nowrap">Name</TableHead>}
                  {visibleColumns.formula && <TableHead className="text-xs whitespace-nowrap">Formula</TableHead>}
                  {visibleColumns.cas && <TableHead className="text-xs whitespace-nowrap">CAS #</TableHead>}
                  {visibleColumns.mw && <TableHead className="text-xs whitespace-nowrap">MW (g/mol)</TableHead>}
                  {bomView === "material" && <TableHead className="text-xs whitespace-nowrap">Steps</TableHead>}
                  {visibleColumns.qty && <TableHead className="text-xs whitespace-nowrap">Qty</TableHead>}
                  {visibleColumns.co2 && <TableHead className="text-xs whitespace-nowrap">CO₂ kg</TableHead>}
                  {visibleColumns.status && <TableHead className="text-xs whitespace-nowrap">Status</TableHead>}
                  {visibleColumns.supplier && <TableHead className="text-xs whitespace-nowrap">Supplier</TableHead>}
                  {visibleColumns.cost && <TableHead className="text-xs whitespace-nowrap">Cost</TableHead>}
                  {visibleColumns.deadline && <TableHead className="text-xs whitespace-nowrap">Deadline</TableHead>}
                  {visibleColumns.delivery && <TableHead className="text-xs whitespace-nowrap">Earliest Delivery</TableHead>}
                  {visibleColumns.packageSize && <TableHead className="text-xs whitespace-nowrap">Package Size</TableHead>}
                  {visibleColumns.units && <TableHead className="text-xs whitespace-nowrap">Units</TableHead>}
                  <TableHead className="w-12 px-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded hover:bg-accent transition-colors">
                          <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                        <DropdownMenuLabel className="text-xs font-semibold">Column Visibility</DropdownMenuLabel>
                        {columnDefs.map(col => (
                          <div key={col.key} className="flex items-center justify-between px-2 py-1.5 text-sm">
                            <span>{col.label}</span>
                            <Switch
                              checked={visibleColumns[col.key]}
                              onCheckedChange={(v) => setVisibleColumns(prev => ({ ...prev, [col.key]: !!v }))}
                              className="scale-75"
                            />
                          </div>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedBomItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center text-sm text-muted-foreground py-8">
                      No materials in BOM. Add chemicals to your synthesis steps first.
                    </TableCell>
                  </TableRow>
                ) : bomView !== "material" ? (
                  activeGroups.map(group => (
                    <Fragment key={group.key}>
                      {renderGroupHeader(group)}
                      {!collapsedGroups[group.key] && group.items.map(item => renderBOMRow(item))}
                    </Fragment>
                  ))
                ) : (
                  sortedBomItems.map(item => renderBOMRow(item, true))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {run.notes && (
          <Card className="mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-muted-foreground">{run.notes}</p></CardContent>
          </Card>
        )}

      </main>

      {/* Full-screen Process Steps overlay */}
      {stepsViewOpen && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Process Steps — {run.title}</h2>
              <Badge variant="outline" className="text-xs">{run.selectedStepIds.length} step{run.selectedStepIds.length !== 1 ? "s" : ""}</Badge>
            </div>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setStepsViewOpen(false)}>
              <X className="h-4 w-4" /> Close
            </Button>
          </div>

          {/* Steps canvas */}
          <div className="flex-1 overflow-auto p-8">
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {project.steps
                .filter(s => run.selectedStepIds.includes(s.id))
                .map((step, i, arr) => {
                  const targetDate = stepTargetDates[step.id];
                  const overrideDeadline = run.stepOverrides?.[step.id]?.deadline;
                  const deadline = overrideDeadline || targetDate;
                  const overrideYield = run.stepOverrides?.[step.id]?.yield;
                  const stepYield = overrideYield ?? (step.yield ? parseFloat(step.yield) : run.assumedYield);
                  const overrideDays = run.stepOverrides?.[step.id]?.stepTimeDays;
                  const stepDays = overrideDays ?? (step.stepTime ? parseInt(step.stepTime) || 0 : 0);

                  return (
                    <div key={step.id} className="flex items-center gap-4 shrink-0">
                      <div
                        className="rounded-lg border bg-card cursor-pointer hover:shadow-md hover:border-primary transition-all overflow-hidden w-[260px]"
                        onClick={() => navigate(`/project/${project.id}/step/${step.id}`)}
                      >
                        <div className="w-full h-[150px] flex items-center justify-center bg-muted/20">
                          <ReactionStructure reactionString={step.reactionString} width={260} height={150} />
                        </div>
                        <div className="px-4 py-3 border-t space-y-1.5">
                          <p className="text-sm font-semibold">{getStepDisplayName(step)}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {stepYield > 0 && (
                              <span>Yield: {Math.round(stepYield)}%</span>
                            )}
                            {stepDays > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {stepDays}d
                              </span>
                            )}
                          </div>
                          {deadline && (
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              {format(parseISO(deadline), "MMM d, yyyy")}
                            </p>
                          )}
                        </div>
                      </div>
                      {i < arr.length - 1 && <ArrowRight className="h-5 w-5 text-muted-foreground/40 shrink-0" />}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      <RunSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        run={run}
        project={project}
        onSave={(updates) => {
          updateRun(run.id, updates);
          regenerateBOM(run.id, project);
          toast({ title: "✓ Settings saved", description: "Run configuration has been updated." });
        }}
      />
    </div>
  );
};

export default RunDetail;
